<template>
  <ContentWrap>
    <el-form :inline="true" :model="q" class="notify-query-form">
      <el-form-item label="接收用户">
        <el-input v-model="q.userNickname" clearable class="!w-180px" placeholder="请输入用户昵称" @keyup.enter="search" />
      </el-form-item>
      <el-form-item label="通知类型">
        <el-select v-model="q.notifyType" clearable class="!w-140px" placeholder="全部类型">
          <el-option v-for="item in notifyTypes" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="search">搜索</el-button>
        <el-button @click="reset">重置</el-button>
        <el-button v-hasPermi="['bosscircle:notify:create']" type="primary" @click="openCreate">发送通知</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column prop="id" label="编号" width="90" />
      <el-table-column label="接收用户" min-width="160">
        <template #default="{ row }">
          <div>{{ row.userNickname }}</div>
        </template>
      </el-table-column>
      <el-table-column label="来源用户" min-width="160">
        <template #default="{ row }">
          <template v-if="row.fromUserId">
            <div>{{ row.fromUserNickname }}</div>
          </template>
          <span v-else class="text-gray-400">系统</span>
        </template>
      </el-table-column>
      <el-table-column label="类型" width="110">
        <template #default="{ row }">
          <el-tag effect="plain">{{ getNotifyTypeLabel(row.notifyType) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="displayContent" label="内容" min-width="260" show-overflow-tooltip />
      <el-table-column label="已读" width="90">
        <template #default="{ row }">
          <el-tag :type="row.isRead ? 'success' : 'info'" effect="light">{{ row.isRead ? '已读' : '未读' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="时间" :formatter="dateFormatter" width="180" />
      <el-table-column label="操作" width="80" fixed="right">
        <template #default="{ row }">
          <el-button v-hasPermi="['bosscircle:notify:delete']" link type="danger" @click="del(row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination v-model:page="q.pageNo" v-model:limit="q.pageSize" :total="total" @pagination="load" />
  </ContentWrap>

  <Dialog v-model="visible" title="发送系统通知">
    <el-form ref="fref" :model="form" :rules="rules" label-width="90px">
      <el-form-item label="接收用户" prop="userId">
        <el-select v-model="form.userId" filterable remote reserve-keyword class="!w-full" placeholder="搜索用户昵称" :remote-method="searchUsers" :loading="userLoading">
          <el-option v-for="user in userOptions" :key="user.userId" :label="userLabel(user)" :value="user.userId" />
        </el-select>
      </el-form-item>
      <el-form-item label="内容" prop="content">
        <el-input v-model="form.content" type="textarea" :rows="4" maxlength="500" show-word-limit />
      </el-form-item>
    </el-form>
    <template #footer><el-button type="primary" @click="submit">发送</el-button></template>
  </Dialog>
</template>

<script setup lang="ts">
import { dateFormatter } from '@/utils/formatTime'
import * as Api from '@/api/business/notify'

defineOptions({ name: 'BossCircleNotify' })

type NotifyUser = { userId: number; nickname: string; company?: string; position?: string }

const notifyTypes = [
  { label: '点赞', value: 1 },
  { label: '评论', value: 2 },
  { label: '回复', value: 3 },
  { label: '关注', value: 4 },
  { label: '好友', value: 5 },
  { label: '系统通知', value: 6 }
]
const message = useMessage()
const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const visible = ref(false)
const fref = ref()
const userLoading = ref(false)
const userOptions = ref<NotifyUser[]>([])
const q = reactive({ pageNo: 1, pageSize: 10, userNickname: '', notifyType: undefined as number | undefined })
const form = reactive({ userId: undefined as number | undefined, content: '' })
const rules = {
  userId: [{ required: true, message: '请选择接收用户', trigger: 'change' }],
  content: [{ required: true, message: '请输入通知内容', trigger: 'blur' }]
}

const getNotifyTypeLabel = (type?: number) => notifyTypes.find((item) => item.value === type)?.label || '未知类型'
const userLabel = (user: NotifyUser) => [user.nickname, `ID: ${user.userId}`, user.company, user.position].filter(Boolean).join(' | ')
const load = async () => {
  loading.value = true
  try {
    const d = await Api.getNotifyPage(q)
    list.value = d.list || []
    total.value = d.total || 0
  } finally {
    loading.value = false
  }
}
const search = () => {
  q.pageNo = 1
  load()
}
const reset = () => {
  q.userNickname = ''
  q.notifyType = undefined
  search()
}
const searchUsers = async (keyword: string) => {
  if (!keyword.trim()) {
    userOptions.value = []
    return
  }
  userLoading.value = true
  try {
    userOptions.value = await Api.getNotifyUserOptions(keyword)
  } finally {
    userLoading.value = false
  }
}
const openCreate = () => {
  form.userId = undefined
  form.content = ''
  userOptions.value = []
  visible.value = true
}
const submit = async () => {
  if (!(await fref.value?.validate().catch(() => false))) return
  await Api.createNotify(form as { userId: number; content: string })
  message.success('发送成功')
  visible.value = false
  load()
}
const del = async (id: number) => {
  await message.confirm('删除后无法恢复，是否继续？', '确认删除')
  await Api.deleteNotify(id)
  message.success('删除成功')
  load()
}

onMounted(load)
</script>
